import React from "react";
import { Download, EllipsisVertical, FolderInput, FolderOpen, Pencil, RefreshCw, Trash2 } from "lucide-react";

import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { menuCls } from "./KbAddMenu";
import { statusOf } from "./listing";

import type { KbNode } from "@/shared/types";

export interface NodeActions {
  /** Rows only: the file view is what Open opens. */
  onOpen?: () => void;
  onDownload?: () => void;
  onRename: () => void;
  onMove: () => void;
  onReindex: () => void;
  onDelete: () => void;
}

const itemCls = "gap-2 px-2 py-[5px] rounded-[8px] text-[13px] cursor-pointer focus:bg-[var(--accent-hover)]";

/** A folder's or file's ⋮: open, download, rename, move, reindex a failed file, delete. */
export function KbNodeMenu({ node, actions, className = "" }: { node: KbNode; actions: NodeActions; className?: string }) {
  // Deferred past Radix's close; the menu also skips its focus-restore, which would blur the rename input.
  const later = (fn: () => void) => () => setTimeout(fn, 0);
  const icon = "w-3.5 h-3.5";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={`Actions for ${node.name}`}
          title="More actions"
          className={`press w-7 h-7 shrink-0 rounded-[8px] flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)] data-[state=open]:bg-[var(--accent-active)] data-[state=open]:text-[var(--text-primary)] ${className}`}
        >
          <EllipsisVertical className="w-4 h-4" strokeWidth={1.75} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={`w-44 ${menuCls}`} onCloseAutoFocus={(e) => e.preventDefault()}>
        {actions.onOpen && (
          <DropdownMenuItem className={itemCls} onSelect={later(actions.onOpen)}>
            <FolderOpen className={icon} strokeWidth={1.75} /> Open
          </DropdownMenuItem>
        )}
        {actions.onDownload && node.kind === "file" && (
          <DropdownMenuItem className={itemCls} onSelect={later(actions.onDownload)}>
            <Download className={icon} strokeWidth={1.75} /> Download
          </DropdownMenuItem>
        )}
        <DropdownMenuItem className={itemCls} onSelect={later(actions.onRename)}>
          <Pencil className={icon} strokeWidth={1.75} /> Rename
        </DropdownMenuItem>
        <DropdownMenuItem className={itemCls} onSelect={later(actions.onMove)}>
          <FolderInput className={icon} strokeWidth={1.75} /> Move
        </DropdownMenuItem>
        {statusOf(node) === "failed" && (
          <DropdownMenuItem className={itemCls} onSelect={later(actions.onReindex)}>
            <RefreshCw className={icon} strokeWidth={1.75} /> Reindex
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="-mx-1 my-1 h-px bg-[var(--hairline)]" />
        <DropdownMenuItem className={`${itemCls} text-[var(--danger)] focus:text-[var(--danger)]`} onSelect={later(actions.onDelete)}>
          <Trash2 className={icon} strokeWidth={1.75} /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
